import type { PropsWithChildren } from 'react'
import React, { useRef } from 'react'

import { css } from '@emotion/react'
import styled from '@emotion/styled'

import { useContextParams } from '@core/params/context'
import { isStories } from '@core/params/helpers/isStories'
import {
  circlePulseAnimation,
  verticalPulseAnimation,
} from '@core/styles/animations'
import {
  THUMBNAIL_HEIGHT,
  THUMBNAIL_PULSE_ANIMATION_COLOR,
  THUMBNAIL_WIDTH,
} from '@core/styles/cssVariables'
import type { EmbedFeedParams } from '@core/types'
import { ThumbnailShapeEnum } from '@core/types'
import { getThumbnailSize } from '@embed/components/helpers/getThumbnailSize'
import {
  EMBED_THUMBNAIL_WRAPPER_INNER,
  THUMBNAIL_CONTAINER,
} from '@player/testIds'

import {
  THUMBNAIL_SIZE_LARGE,
  THUMBNAIL_SIZE_MEDIUM,
  THUMBNAIL_SIZE_SMALL,
} from '../../styled'

import { useFocusableGate } from './hooks/useFocusableGate'
import { useSlideRef } from './hooks/useSlideRef'

const [WIDTH_MEDIUM, HEIGHT_MEDIUM] = THUMBNAIL_SIZE_MEDIUM
const [WIDTH_SMALL, HEIGHT_SMALL] = THUMBNAIL_SIZE_SMALL
const [WIDTH_LARGE, HEIGHT_LARGE] = THUMBNAIL_SIZE_LARGE

const STORIES_BORDER_RADIUS: Record<ThumbnailShapeEnum, string> = {
  [ThumbnailShapeEnum.CIRCLE]: '50%',
  [ThumbnailShapeEnum.RECTANGLE]: '8px',
  [ThumbnailShapeEnum.SQUARE]: '8px',
}

// Shape defaults are fallbacks on the thumbnail rather than `--params-*`
// variables on the host, so they don't leak into the player rendered in the
// same shadow root (e.g. product cards also read `--thumbnail-border-radius`).
const getStoriesBorderRadius = (shape?: ThumbnailShapeEnum): string =>
  `var(--thumbnail-border-radius, ${shape ? STORIES_BORDER_RADIUS[shape] : '0px'})`

const isOneToOneShape = (shape?: ThumbnailShapeEnum): boolean =>
  shape === ThumbnailShapeEnum.CIRCLE || shape === ThumbnailShapeEnum.SQUARE

/**
 * Thumbnail wrapper to provide 1,65 aspect ratio
 * for its children. Pinned and row mode uses exact size 154x250.
 */

// Helper function for conditional :before
const getPulseStyling = (props: any) =>
  props.shouldPulse && props.pulseAnimation
    ? css`
        &:before {
          --pulse-border-radius: ${getStoriesBorderRadius(props.thumbnailShape)};
          content: '';
          position: absolute;
          width: 100%;
          height: 100%;
          border: 1px solid var(${THUMBNAIL_PULSE_ANIMATION_COLOR}, #000);
          top: 50%;
          left: 50%;
          z-index: -1;
          // animation: ${circlePulseAnimation} 3000ms infinite;
          animation: ${
            props.thumbnailShape === ThumbnailShapeEnum.CIRCLE
              ? circlePulseAnimation
              : verticalPulseAnimation
          }
            3000ms infinite;
          will-change: transform, opacity, border-width;
          border-radius: var(--pulse-border-radius);
          box-sizing: border-box;
        }

        // do not animate if user has reduced motion
        @media (prefers-reduced-motion: reduce) {
          &:before {
            animation: none;
            border: none;
          }
        }
      `
    : ''

const Inner = styled.div<{
  width: number
  height: number
  displayMode: EmbedFeedParams['mode']
  hasHorizontalLayout?: boolean
  isStoriesLayout: boolean
  thumbnailShape?: ThumbnailShapeEnum
  circle?: boolean
  responsive?: boolean
  shouldPulse?: boolean
  pulseAnimation?: boolean
  productCarouselBottomLayout?: boolean
}>`
  box-sizing: border-box;
  padding-top: 0;
  position: relative;

  // Keyboard focus ring around the whole slide. The focusable element is the
  // play button inside the card; ':has()' lifts its focus-visible state up to
  // the card so the ring frames the slide even when the play icon is hidden
  // (e.g. while the preview is playing). '.focus-visible' covers the Safari/iOS
  // focus-visible polyfill. Browsers without ':has()' simply drop this rule,
  // which degrades to no ring without affecting any other styling.
  //
  // The ring is drawn as an overlay pseudo-element rather than 'outline':
  //  - A positive outline-offset lands in the scrolling track's clipped region
  //    (overflow: scroll hidden), so it barely shows.
  //  - A negative offset draws inside the card, but the '.inner > .outer' layer
  //    below has 'transform: translateZ(0)' (its own stacking context) and
  //    fully covers the card, painting *over* an inset outline.
  // A positioned '::after' with a higher z-index sits above that content layer
  // and inside the slide box, so the ring is fully visible on every edge.
  &:has(:focus-visible)::after,
  &:has(.focus-visible)::after {
    content: '';
    position: absolute;
    inset: 0;
    box-sizing: border-box;
    border: 2px solid var(--thumbnail-focus-outline-color, #1a73e8);
    border-radius: ${(props) =>
      props.isStoriesLayout
        ? getStoriesBorderRadius(props.thumbnailShape)
        : props.thumbnailShape === ThumbnailShapeEnum.CIRCLE
          ? '50%'
          : 'var(--border-radius, 8px)'};
    pointer-events: none;
    z-index: 3;
  }

  ${getPulseStyling}

  ${(props) => `
    ${
      props.responsive
        ? `
        width: 100%;
        aspect-ratio: var(--thumbnail-aspect-ratio, ${props.width} / ${props.height});`
        : `
      aspect-ratio: var(--thumbnail-aspect-ratio, ${props.width} / ${props.height});
      // if it's a circle we want to ensure both dimensions are the width
      // so it maintains a 1/1 aspect ratio for the border radius to always be a circle
      flex: 0 0 var(${props.thumbnailShape === ThumbnailShapeEnum.CIRCLE ? THUMBNAIL_WIDTH : THUMBNAIL_WIDTH}, ${props.width}px);
      height: ${
        isOneToOneShape(props.thumbnailShape)
          ? `var(${THUMBNAIL_HEIGHT}, var(${THUMBNAIL_WIDTH}, ${props.height}px))`
          : `var(${THUMBNAIL_HEIGHT}, ${props.height}px)`
      };
      width: var(${THUMBNAIL_WIDTH}, ${props.width}px);
    `
    }
  `}

  // Horizontal layout if params.thumbnail_style is horizontal
  ${(props) =>
    props.hasHorizontalLayout &&
    `
    aspect-ratio: 16/9;
    flex: 0 0 auto;
    height: var(${THUMBNAIL_HEIGHT}, 240px);
    width: auto;
  `}

  ${(props) =>
    props.displayMode === 'grid' &&
    !props.productCarouselBottomLayout &&
    `
    flex: 0;
    height: 0;
    margin-left: 0;
    margin-right: 0;
    padding-top: 165%;
    width: 100%;
  `}
`

const Outer = styled.div<{
  isStoriesLayout: boolean
  showV2Card: boolean
  hasInteractionWithPlayerDeck?: boolean
  thumbnailShape?: ThumbnailShapeEnum
}>`
  border-radius: var(--border-radius);
  ${({ hasInteractionWithPlayerDeck }) =>
    hasInteractionWithPlayerDeck
      ? `
    border-radius: var(--border-radius) var(--border-radius) 0 0;
  `
      : ''}
  ${({ showV2Card }) =>
    showV2Card
      ? `
    border-radius: 0;
  `
      : ''}

  ${({ isStoriesLayout, thumbnailShape }) =>
    isStoriesLayout
      ? `border-radius: ${getStoriesBorderRadius(thumbnailShape)};`
      : ''}

  // [CS-2501] Do not remove the "url" part of the background-image property.
  // It serves to improve the LCP web vital by enabling <Placeholder> as a LCP candidate.
  background-image: url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAPAAAAGQBAMAAABi+PBPAAAALVBMVEXOzs7T09PW1tbZ2dni4uLf39/s7OzQ0NDo6Ojk5OTb29vq6urm5ubd3d3u7u7SSsE5AAACR0lEQVR42u3TMUorURgFYDvrTGP9MjswXepYpH+WaW0CopVbEGJpI9jZCbYhkA1EsE0j2KbMGszcfybjVQloJeT77wIO5x6+g/OzuHm6yXXcS7r7u7jB9k7re0v39BjX2163vc72Lq/SHR5kt16vVil5Ok7Rs1lKXtxE9HNKHt5G7v/B4OQkJS9HEf2akvsPkVv2esfHKbYoukWT2+kcHaXoi4uPwW3jKrltXCW3jZvkpvFo86JyNG6St4Wr3H9N47jPjdf1b4+z347GL88R3eRW0VF5tEyVXyO6bVyWdW632+R2onL+2z/YOGsc92XjMtu42L3xeb3xNKLrjRcRXWcPs43TyLFx/dn9fONo3ORW0Ts2Hs+nbeXNwouoXDcefmq83Ly08VM07reNy8gtssaXOzaOxvNZRC+yjYdfNl5movr5xnnjb3/77zieTDLH93vgeLZ3jiccc8wxxxxzzDHHHHPMMcccc8wxxxxzzDHHHHPMMcccc8wxxxxzzDHHHHPMMcccc8wxxxxzzDHHHHPMMcccc8wxxxxzzDHHHHPMMcccc8wxxxxzzDHHHHPMMcccc8wxxxxzzDHHHHPMMcccc8wxxxxzzDHHHHPMMcccc8wxxxxzzDHHHHPMMcccc8wxxxxzzDHHHHPMMcccc8wxxxxzzDHHHHPMMcccc8wxxxxzzDHHHHPMMcccc8wxxxxzzDHHHHPMMcccc8wxxxxzzDHHHHPMMcccc8wxxxxzzDHHHHPMMcd77Hj1I8eR/SvHhx9++x1urtNRptEvkAAAAABJRU5ErkJggg==');
  background-size: cover;

  box-sizing: border-box;
  height: 100%;
  left: 0;
  overflow: hidden;
  position: absolute;
  top: 0;
  transform: translateZ(0);
  width: 100%;
`

type IProps = React.HTMLAttributes<HTMLDivElement> & {
  mode: EmbedFeedParams['mode']
  isLarge: boolean
  isSmall?: boolean
  label?: string
  isPlaceholder?: boolean
  isInViewport?: boolean
  showV2Card?: boolean
  productCarouselBottomLayout?: boolean
  hasInteractionWithPlayerDeck?: boolean
  hasHorizontalLayout?: boolean
  responsive?: boolean
  shouldPulse?: boolean
  /**
   * Set by the parent when this wrapper is the slide root (no player deck card
   * around it), so the slide's visibility is measured on the same element this
   * component gates.
   */
  observeRef?: React.Ref<HTMLDivElement>
}

/**
 *
 */
const ThumbnailWrapper = ({
  mode,
  isLarge,
  isSmall,
  children,
  label,
  isPlaceholder,
  isInViewport,
  showV2Card,
  productCarouselBottomLayout = false,
  hasInteractionWithPlayerDeck,
  hasHorizontalLayout,
  responsive,
  shouldPulse,
  observeRef,
  ...rest
}: PropsWithChildren<IProps>): React.ReactElement<any> => {
  const params = useContextParams()
  const [thumbVideoWidth, thumbVideoHeight] = getThumbnailSize(params.size)
  // Large carousel experiment
  const width = productCarouselBottomLayout
    ? thumbVideoWidth
    : isLarge
      ? WIDTH_LARGE
      : isSmall
        ? WIDTH_SMALL
        : WIDTH_MEDIUM
  const height = productCarouselBottomLayout
    ? thumbVideoHeight
    : isLarge
      ? HEIGHT_LARGE
      : isSmall
        ? HEIGHT_SMALL
        : HEIGHT_MEDIUM

  const { thumbnail_shape, pulse_animation } = params
  const isStoriesLayout = isStories(params)

  const innerRef = useRef<HTMLDivElement | null>(null)

  // The gate needs this element and, when this wrapper is the slide root, so
  // does the parent's visibility observer.
  const setInner = useSlideRef(innerRef, observeRef)

  // Keyboard focus containment for the slide: while it is off-screen every
  // focusable descendant leaves the tab order, in lockstep with the
  // `aria-hidden` set on the same element below.
  useFocusableGate(innerRef, isInViewport)

  return (
    <Inner
      ref={setInner}
      {...(isPlaceholder
        ? { 'aria-hidden': true }
        : {
            role: 'group',
            'aria-label': label || 'Thumbnail',
            ...(isInViewport === false ? { 'aria-hidden': true as const } : {}),
          })}
      displayMode={mode}
      height={isOneToOneShape(thumbnail_shape) ? width : height}
      width={width}
      hasHorizontalLayout={hasHorizontalLayout}
      isStoriesLayout={isStoriesLayout}
      thumbnailShape={thumbnail_shape}
      data-testid={EMBED_THUMBNAIL_WRAPPER_INNER}
      responsive={responsive}
      productCarouselBottomLayout={productCarouselBottomLayout}
      shouldPulse={shouldPulse}
      pulseAnimation={pulse_animation}
      {...rest}
    >
      <Outer
        isStoriesLayout={isStoriesLayout}
        showV2Card={!!showV2Card && !isPlaceholder}
        data-testid={THUMBNAIL_CONTAINER}
        hasInteractionWithPlayerDeck={hasInteractionWithPlayerDeck}
        thumbnailShape={thumbnail_shape}
      >
        {children}
      </Outer>
    </Inner>
  )
}

export default ThumbnailWrapper
